import type { DeleteQuestionnaireTemplateMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { DeleteQuestionnaireTemplateDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

import type { FrontendRouterInputs } from '../routerInputs';

type DeleteQuestionnaireTemplateInput =
  FrontendRouterInputs['questionnaireTemplate']['delete'];
type DeleteQuestionnaireTemplateMutationVariables = {
  Id: DeleteQuestionnaireTemplateInput['id'];
};

/**
 * Maps the TRPC delete response to match the GraphQL mutation structure.
 *
 * The GraphQL `deleteQuestionnaireTemplate` mutation reported an affected-row
 * count for the version cascade (`delete_questionnaire_template_version`) plus
 * the deleted row (`delete_questionnaire_template_by_pk`). The tRPC backend is
 * a single delete returning void; the version cascade happens server-side. We
 * surface a single deleted template (id-based API) and a zero version count,
 * matching the legacy shape consumers branch on.
 */
const mapTrpcResponseToGraphQL = (
  id: string
): DeleteQuestionnaireTemplateMutation => ({
  delete_questionnaire_template_version: {
    __typename: 'questionnaire_template_version_mutation_response',
    affected_rows: 0,
  },
  delete_questionnaire_template_by_pk: {
    __typename: 'questionnaire_template',
    Id: id,
  },
});

export const useDeleteQuestionnaireTemplate = createMutationHook({
  name: 'deleteQuestionnaireTemplate',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.questionnaireTemplate.delete.mutationOptions({ onSuccess }),
  mapInput: (input: {
    variables: DeleteQuestionnaireTemplateMutationVariables;
  }): DeleteQuestionnaireTemplateInput => ({ id: input.variables.Id }),
  invalidate: (trpc) => [
    trpc.frontend.questionnaireTemplate.register.queryKey(),
    trpc.frontend.questionnaireTemplate.getById.queryKey(),
  ],
  mapResponse: (
    _result,
    input: { variables: DeleteQuestionnaireTemplateMutationVariables }
  ): DeleteQuestionnaireTemplateMutation =>
    mapTrpcResponseToGraphQL(input.variables.Id),
  graphqlDocument: DeleteQuestionnaireTemplateDocument,
  graphqlVariables: (input: {
    variables: DeleteQuestionnaireTemplateMutationVariables;
  }) => input.variables,
  evictFields: ['questionnaire_template', 'questionnaire_template_by_pk'],
});
